import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import _ from 'lodash';
import { FilterWithSwapOption } from '../../../../common/components/DataTable/DataTableFilters';
import { IMainTable } from '../../../../common/models/common';
import dataRetentionLogService from '../../../../services/master/dataRetentionLog/dataRetentionLog.service';
import DataTable from '../../../../common/components/DataTable';
import { showDateFromApi } from '../../../../common/helperFunction';
import {
  dataRetentionLogSelector,
  setTableColumnSelection,
} from '../../../../store/master/dataRetentionLog/dataRetentionLog.reducer';
import { searchDataRetentionLog } from '../../../../store/master/dataRetentionLog/dataRetentionLog.action';

const MainTable: React.ForwardRefRenderFunction<unknown, IMainTable> = (_props, ref) => {
  const dataTableRef = useRef(null);
  const [ObjectForColumnFilter, setObjectForColumnFilter] = useState({});

  useImperativeHandle(ref, () => ({
    refreshData() {
      dataTableRef?.current.refreshData();
    },
  }));

  // Dropdown (distinct values) for category columns, text for free text and dates. Values come
  // from this page's own /column-lookup endpoint: these tables are deliberately not registered
  // with the generic column-lookup / import / bulk-update endpoints.
  const DROPDOWN_COLUMNS = [
    'run_start_time',
    'step_name',
    'table_name',
    'status',
    'what_if',
    'cutoff',
  ];
  const getColumnLookup = (filter: any) => dataRetentionLogService.getColumnLookup(filter);
  const FilterText = (dataIndex: string, form) => {
    const isDropdown = DROPDOWN_COLUMNS.includes(dataIndex);
    return FilterWithSwapOption(
      dataIndex,
      '',
      form,
      isDropdown ? getColumnLookup : null,
      ObjectForColumnFilter,
      false,
      false,
      false,
      !isDropdown
    );
  };

  const column = (title: string, dataIndex: string, form, extra: { [key: string]: any } = {}) => ({
    title: <span className="dragHandler">{title}</span>,
    column: dataIndex,
    sorter: true,
    ellipsis: true,
    children: [
      {
        title: FilterText(dataIndex, form),
        dataIndex,
        key: dataIndex,
        ellipsis: true,
        ...extra,
      },
    ],
  });

  const dateTime = { render: (date: Date) => (!_.isNil(date) ? showDateFromApi(date, true) : '') };

  const getTableColumns = (form) => {
    return [
      column('Run Started', 'run_start_time', form, dateTime),
      column('Step', 'step_name', form, {
        // The run's own row: totals for the whole run.
        render: (value: string) => (value === 'Run Summary' ? <strong>{value}</strong> : value),
      }),
      column('Table', 'table_name', form),
      column('Status', 'status', form),
      column('Rows', 'rows_affected', form, {
        render: (value: number) => (!_.isNil(value) ? Number(value).toLocaleString() : ''),
      }),
      column('Cutoff', 'cutoff', form, {
        render: (date: Date) => (!_.isNil(date) ? showDateFromApi(date) : ''),
      }),
      column('Dry Run', 'what_if', form, {
        render: (value: boolean) => (!_.isNil(value) ? (value ? 'Yes' : 'No') : ''),
      }),
      column('Message', 'message', form),
      column('Step Start Time', 'start_time', form, dateTime),
      column('End Time', 'end_time', form, dateTime),
      column('Run Id', 'run_id', form),
      column('Id', 'id', form),
    ];
  };

  return (
    <>
      <DataTable
        ref={dataTableRef}
        showAddButton={false}
        showDelete={false}
        showBulkUpdate={false}
        hideExportButton={true}
        disableRowSelection={true}
        getTableColumns={getTableColumns}
        reduxSelector={dataRetentionLogSelector}
        searchTableData={searchDataRetentionLog}
        setTableColumnSelection={setTableColumnSelection}
        setObjectForColumnFilter={setObjectForColumnFilter}
        globalSearchExist={false}
      />
    </>
  );
};

export default forwardRef(MainTable);
